import type { WalletBenefit } from "../wallet/types";
import type { Profile } from "./profile";

export type BucketId = "daily" | "weekly" | "travel" | "monthly" | "yearly";
export type ConfidenceTier = "high" | "medium" | "low" | "unknown";

export type BucketDef = {
  id: BucketId;
  emoji: string;
  title: string;
  verb: string;
  cadence: string;
  description: string;
};

export const BUCKETS: BucketDef[] = [
  {
    id: "daily",
    emoji: "🪪",
    title: "随手刷",
    verb: "刷",
    cadence: "每天",
    description: "默认卡兜底，根本不用打开 App"
  },
  {
    id: "weekly",
    emoji: "☕",
    title: "这周薅",
    verb: "薅",
    cadence: "每周",
    description: "高频活动，做一下就有"
  },
  {
    id: "travel",
    emoji: "🧳",
    title: "出门带",
    verb: "带",
    cadence: "出差/旅行触发",
    description: "贵宾厅、接送机、酒店礼遇"
  },
  {
    id: "monthly",
    emoji: "🗓️",
    title: "月初约",
    verb: "约",
    cadence: "每月一次",
    description: "洗牙、体检、SPA、快剪等预约型"
  },
  {
    id: "yearly",
    emoji: "📊",
    title: "年度算",
    verb: "算",
    cadence: "每年一次",
    description: "续卡、刷免、降级 / 销卡"
  }
];

export function confidenceTier(b: WalletBenefit): ConfidenceTier {
  const c = (b.confidence || "").toLowerCase();
  if (c === "high" || c === "medium" || c === "low") return c;
  return "unknown";
}

export function isTrustedForMainList(b: WalletBenefit): boolean {
  const t = confidenceTier(b);
  return t === "high" || t === "medium";
}

export function isTrustedForScoring(b: WalletBenefit): boolean {
  return confidenceTier(b) === "high";
}

export function confidenceLabel(t: ConfidenceTier): string {
  if (t === "high") return "高置信";
  if (t === "medium") return "中置信";
  if (t === "low") return "待核实";
  return "待核验";
}

export function confidenceBadgeVariant(t: ConfidenceTier): "default" | "secondary" | "outline" | "destructive" {
  if (t === "high") return "default";
  if (t === "medium") return "secondary";
  return "outline";
}

export function bucketOf(b: WalletBenefit): BucketId {
  const cat = b.category;
  const kind = b.kind;

  if (cat === "airport_lounge" || cat === "airport_transfer" || cat === "insurance" || cat === "miles" || cat === "road_assist") {
    return "travel";
  }
  if (cat === "hotel") {
    return "travel";
  }
  // Health / spa / golf 等需要主动预约 → 月初约
  if (cat === "health" || cat === "spa" || cat === "golf") {
    return "monthly";
  }
  if (kind === "privilege" && (cat === "dining")) {
    return "monthly";
  }
  if (kind === "cashback_lottery" || kind === "discount" || kind === "cashback") {
    return "weekly";
  }
  if (cat === "dining" || cat === "shopping" || cat === "fuel" || cat === "cashback" || cat === "rideshare" || cat === "entertainment" || cat === "coupon") {
    return "weekly";
  }
  return "weekly";
}

export function isBucketVisible(bucket: BucketId, profile: Profile, hasContent: boolean): boolean {
  if (bucket === "daily") return true;
  if (bucket === "yearly") return true;

  if (bucket === "travel") {
    if (profile.flights === "none" && profile.hotels === "none") return false;
    return true;
  }
  if (bucket === "monthly") {
    if (profile.bookingWill === "none") return false;
    return hasContent;
  }
  if (bucket === "weekly") {
    return hasContent;
  }
  return hasContent;
}

const SCENARIO_TO_CATEGORIES: Record<string, string[]> = {
  dining: ["dining", "shopping", "cashback", "coupon"],
  delivery_rideshare: ["shopping", "dining", "rideshare", "cashback", "coupon"],
  grocery: ["shopping", "cashback", "coupon"],
  fuel: ["shopping", "fuel", "cashback"],
  online_shop: ["shopping", "cashback", "coupon"],
  reimburse: ["shopping", "cashback"]
};

export function isBenefitRelevantToProfile(b: WalletBenefit, profile: Profile): boolean {
  const cat = b.category;
  if (cat === "airport_lounge" || cat === "airport_transfer" || cat === "miles") {
    return profile.flights !== "none";
  }
  if (cat === "hotel") {
    return profile.hotels !== "none";
  }
  if (cat === "health" || cat === "spa" || cat === "golf") {
    return profile.bookingWill !== "none";
  }
  if (cat === "dining" || cat === "shopping" || cat === "fuel" || cat === "rideshare" || cat === "entertainment" || cat === "cashback" || cat === "coupon") {
    if (profile.scenarios.length === 0) return true;
    return profile.scenarios.some(sc => (SCENARIO_TO_CATEGORIES[sc] || []).includes(cat));
  }
  return true;
}

export type BucketSummary = {
  def: BucketDef;
  visible: boolean;
  benefits: WalletBenefit[];
  pendingReview: WalletBenefit[];
  hiddenCount: number;
};

export function summarizeBuckets(benefits: WalletBenefit[], profile: Profile): BucketSummary[] {
  const trusted: Record<BucketId, WalletBenefit[]> = {
    daily: [],
    weekly: [],
    travel: [],
    monthly: [],
    yearly: []
  };
  const pending: Record<BucketId, WalletBenefit[]> = {
    daily: [],
    weekly: [],
    travel: [],
    monthly: [],
    yearly: []
  };
  const hidden: Record<BucketId, number> = { daily: 0, weekly: 0, travel: 0, monthly: 0, yearly: 0 };

  for (const b of benefits) {
    const id = bucketOf(b);
    if (!isBenefitRelevantToProfile(b, profile)) {
      hidden[id] += 1;
      continue;
    }
    if (isTrustedForMainList(b)) {
      trusted[id].push(b);
    } else {
      pending[id].push(b);
    }
  }

  const sortFn = (a: WalletBenefit, b: WalletBenefit) => {
    const order = (c: string) => (c === "high" ? 0 : c === "medium" ? 1 : c === "low" ? 2 : 3);
    const cd = order(a.confidence) - order(b.confidence);
    if (cd !== 0) return cd;
    return (b.estimateValueCny || 0) - (a.estimateValueCny || 0);
  };

  for (const id of Object.keys(trusted) as BucketId[]) {
    trusted[id].sort(sortFn);
    pending[id].sort(sortFn);
  }

  return BUCKETS.map(def => {
    const totalShown = trusted[def.id].length + pending[def.id].length;
    return {
      def,
      benefits: trusted[def.id],
      pendingReview: pending[def.id],
      hiddenCount: hidden[def.id],
      visible: isBucketVisible(def.id, profile, totalShown > 0)
    };
  });
}
